import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';

/**
 * Controlled dialogs have no Radix Trigger, so remember what had focus when the dialog opened and give it back on
 * close (WCAG 2.4.3).
 */
function useReturnFocus(open: boolean) {
  const opener = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (open && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) opener.current = document.activeElement;
  }, [open]);
  return (event: Event) => {
    if (opener.current?.isConnected) {
      event.preventDefault();
      opener.current.focus();
    }
    opener.current = null;
  };
}

interface DialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}

/** Modal dialog: focus trap, Escape and overlay click close it, focus returns to the trigger. */
export function Modal({ open, onOpenChange, title, description, children, footer }: DialogBaseProps) {
  const returnFocus = useReturnFocus(open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bb-overlay" />
        <DialogPrimitive.Content className="bb-modal" onCloseAutoFocus={returnFocus}>
          <DialogPrimitive.Title className="bb-modal__title">{title}</DialogPrimitive.Title>
          {description ? <DialogPrimitive.Description>{description}</DialogPrimitive.Description> : <DialogPrimitive.Description className="bb-visually-hidden">{title}</DialogPrimitive.Description>}
          {children}
          {footer && <div className="bb-modal__actions">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Side panel for details and history (same behaviour as Modal). */
export function Drawer({ open, onOpenChange, title, description, children, footer }: DialogBaseProps) {
  const returnFocus = useReturnFocus(open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bb-overlay" />
        <DialogPrimitive.Content className="bb-drawer" onCloseAutoFocus={returnFocus}>
          <DialogPrimitive.Title className="bb-modal__title">{title}</DialogPrimitive.Title>
          {description ? <DialogPrimitive.Description>{description}</DialogPrimitive.Description> : <DialogPrimitive.Description className="bb-visually-hidden">{title}</DialogPrimitive.Description>}
          {children}
          {footer && <div className="bb-modal__actions">{footer}</div>}
          <DialogPrimitive.Close asChild>
            <Button variant="ghost">Close</Button>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  busy?: boolean;
  /** Destructive actions (revoke, stop) get the danger style. */
  tone?: 'danger' | 'primary';
}

/** Confirmation of a disruptive action (role alertdialog; Cancel is focused first). */
export function ConfirmDialog({ open, onOpenChange, title, children, confirmLabel, onConfirm, busy, tone = 'danger' }: ConfirmDialogProps) {
  const returnFocus = useReturnFocus(open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bb-overlay" />
        <DialogPrimitive.Content className="bb-modal" role="alertdialog" onCloseAutoFocus={returnFocus}>
          <DialogPrimitive.Title className="bb-modal__title">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description asChild>
            <div>{children}</div>
          </DialogPrimitive.Description>
          <div className="bb-modal__actions">
            <DialogPrimitive.Close asChild>
              <Button autoFocus>Cancel</Button>
            </DialogPrimitive.Close>
            <Button variant={tone} onClick={onConfirm} loading={busy}>
              {confirmLabel}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** `label` names the popover for assistive technology (it is a dialog). `open`/`onOpenChange` make it controlled (menus close on choice). */
export function Popover({ trigger, children, label, open, onOpenChange }: { trigger: ReactNode; children: ReactNode; label?: string; open?: boolean; onOpenChange?: (open: boolean) => void }) {
  return (
    <PopoverPrimitive.Root {...(open !== undefined ? { open } : {})} {...(onOpenChange ? { onOpenChange } : {})}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content className="bb-popover" sideOffset={4} aria-label={label}>
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Supplementary hint only — never the sole place of important information. */
export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content className="bb-tooltip" sideOffset={4}>
            {content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
